import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rptService } from '../../../services/rptService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Home as HomeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const SettingsPage = () => {
  const navigate = useNavigate();

  // Amnesty states
  const [amnestyRecords, setAmnestyRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAmnesty, setEditingAmnesty] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    tax_year_from: new Date().getFullYear(),
    tax_year_to: new Date().getFullYear(),
    penalty_percent: 0,
    description: ''
  });

  // Alert state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    loadAmnestyRecords();
  }, []);

  const loadAmnestyRecords = async () => {
    setLoading(true);
    try {
      const result = await rptService.getAllAmnesty();
      if (result.success) {
        setAmnestyRecords(result.data);
      }
    } catch (error) {
      console.error('Error loading amnesty records:', error);
      showSnackbar('Failed to load amnesty records', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const handleOpenModal = (amnesty = null) => {
    if (amnesty) {
      setEditingAmnesty(amnesty);
      setFormData({
        tax_year_from: amnesty.tax_year_from,
        tax_year_to: amnesty.tax_year_to,
        penalty_percent: amnesty.penalty_percent,
        description: amnesty.description || ''
      });
    } else {
      setEditingAmnesty(null);
      setFormData({
        tax_year_from: new Date().getFullYear(),
        tax_year_to: new Date().getFullYear(),
        penalty_percent: 0,
        description: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAmnesty(null);
    setFormData({
      tax_year_from: new Date().getFullYear(),
      tax_year_to: new Date().getFullYear(),
      penalty_percent: 0,
      description: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'tax_year_from' || name === 'tax_year_to') ? parseInt(value) : (name === 'penalty_percent' ? parseFloat(value) : value)
    }));
  };

  const handleSaveAmnesty = async () => {
    // Validation
    if (!formData.tax_year_from || !formData.tax_year_to) {
      showSnackbar('Please enter both start and end years', 'warning');
      return;
    }

    if (formData.tax_year_from > formData.tax_year_to) {
      showSnackbar('Start year must be less than or equal to end year', 'warning');
      return;
    }

    if (formData.penalty_percent < 0 || formData.penalty_percent > 100) {
      showSnackbar('Penalty percent must be between 0 and 100', 'warning');
      return;
    }

    // Check for duplicate amnesty - no year should be covered twice
    const excludeId = editingAmnesty ? editingAmnesty.amnesty_id : null;
    const conflicts = checkForYearConflicts(formData.tax_year_from, formData.tax_year_to, excludeId);
    
    if (conflicts.length > 0) {
      const conflictYears = [...new Set(conflicts.map(c => c.year))].sort((a, b) => a - b);
      const conflictRanges = [...new Set(conflicts.map(c => c.range))];
      
      let message = `The following years are already covered by existing amnesty:\n\n`;
      message += `Years: ${conflictYears.join(', ')}\n`;
      message += `Existing amnesty ranges: ${conflictRanges.join(', ')}\n\n`;
      message += `Please choose a different year range that doesn't overlap with existing amnesty.`;
      
      showSnackbar(message, 'warning');
      return;
    }

    try {
      let result;
      if (editingAmnesty) {
        result = await rptService.updateAmnesty(editingAmnesty.amnesty_id, {
          tax_year_from: formData.tax_year_from,
          tax_year_to: formData.tax_year_to,
          penalty_percent: formData.penalty_percent,
          description: formData.description,
          updated_at: new Date().toISOString()
        });
      } else {
        result = await rptService.addAmnesty({
          tax_year_from: formData.tax_year_from,
          tax_year_to: formData.tax_year_to,
          penalty_percent: formData.penalty_percent,
          description: formData.description
        });
      }

      if (result.success) {
        const yearRange = formData.tax_year_from === formData.tax_year_to 
          ? formData.tax_year_from 
          : `${formData.tax_year_from}-${formData.tax_year_to}`;
        
        showSnackbar(`Amnesty for years ${yearRange} ${editingAmnesty ? 'updated' : 'added'} successfully!`, 'success');
        handleCloseModal();
        loadAmnestyRecords();
      } else {
        showSnackbar(result.error || 'Failed to save amnesty', 'error');
      }
    } catch (error) {
      console.error('Error saving amnesty:', error);
      showSnackbar('Failed to save amnesty record', 'error');
    }
  };

  const handleDeleteAmnesty = async (amnesty) => {
    const yearRange = amnesty.tax_year_from === amnesty.tax_year_to 
      ? amnesty.tax_year_from 
      : `${amnesty.tax_year_from}-${amnesty.tax_year_to}`;
    
    showConfirm(
      'Confirm Delete',
      `Delete amnesty for years ${yearRange}? This action cannot be undone.`,
      async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        try {
          const result = await rptService.deleteAmnesty(amnesty.amnesty_id);
          if (result.success) {
            showSnackbar(`Amnesty for years ${yearRange} deleted successfully!`, 'success');
            loadAmnestyRecords();
          } else {
            showSnackbar(result.error || 'Failed to delete amnesty', 'error');
          }
        } catch (error) {
          console.error('Error deleting amnesty:', error);
          showSnackbar('Failed to delete amnesty record', 'error');
        }
      }
    );
  };

  const currentYear = new Date().getFullYear();
  
  const isYearInRange = (year, amnesty) => {
    return year >= amnesty.tax_year_from && year <= amnesty.tax_year_to;
  };

  const checkForYearConflicts = (fromYear, toYear, excludeAmnestyId = null) => {
    const conflicts = [];
    
    for (let year = fromYear; year <= toYear; year++) {
      const coveringAmnesty = amnestyRecords.find(a => {
        // Skip the amnesty being edited
        if (excludeAmnestyId && a.amnesty_id === excludeAmnestyId) {
          return false;
        }
        return isYearInRange(year, a);
      });
      
      if (coveringAmnesty) {
        conflicts.push({
          year,
          amnesty: coveringAmnesty,
          range: `${coveringAmnesty.tax_year_from}-${coveringAmnesty.tax_year_to}`
        });
      }
    }
    
    return conflicts;
  };

  const handleExport = () => {
    if (amnestyRecords.length === 0) {
      showSnackbar('No amnesty records to export', 'warning');
      return;
    }

    const csvContent = [
      ['Start Year', 'End Year', 'Penalty Percent', 'Description', 'Effective Date'],
      ...amnestyRecords.map(a => [
        a.tax_year_from,
        a.tax_year_to,
        a.penalty_percent,
        `"${(a.description || '').replace(/"/g, '""')}"`,
        `"${new Date(a.effective_date).toLocaleDateString()}"`
      ])
    ].map(e => e.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `amnesty_records_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: '0.875rem' }}>
              <Link 
                underline="hover" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/portal/dashboard')}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Portal
              </Link>
              <Link 
                underline="hover" 
                color="inherit" 
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate('/portal/property-tax/dashboard')}
              >
                RPT System
              </Link>
              <Typography color="text.primary" sx={{ fontWeight: 500 }}>Settings</Typography>
            </Breadcrumbs>
            
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1
            }}>
              <SettingsIcon fontSize="large" sx={{ color: '#3b82f6' }} /> RPT Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
              Manage tax amnesty, penalty overrides, and system configurations.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || amnestyRecords.length === 0}
              sx={{ height: 48, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ 
                height: 48, 
                bgcolor: '#3b82f6', 
                '&:hover': { bgcolor: '#2563eb' },
                boxShadow: '0 4px 6px -1px rgb(59 130 246 / 0.5)'
              }}
            >
              Add Amnesty
            </Button>
          </Box>
        </Box>

        <Card variant="outlined" sx={{ boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ width: 4, height: 24, bgcolor: 'info.main', borderRadius: 1 }} />
              Tax Amnesty Management
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <Typography>Loading amnesty records...</Typography>
              </Box>
            ) : amnestyRecords.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'info.lighter', borderRadius: 2, border: '1px dashed', borderColor: 'info.light' }}>
                <InfoIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography color="info.dark">No amnesty records found. Click "Add Amnesty" to create one.</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                      <TableCell>Year Range</TableCell>
                      <TableCell>Penalty %</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Effective Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {amnestyRecords.map(amnesty => {
                      const yearRange = amnesty.tax_year_from === amnesty.tax_year_to 
                        ? amnesty.tax_year_from 
                        : `${amnesty.tax_year_from} - ${amnesty.tax_year_to}`;
                      const isCurrentYearInRange = isYearInRange(currentYear, amnesty);
                      
                      return (
                        <TableRow 
                          key={amnesty.amnesty_id} 
                          hover
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: '#ffffff' },
                            '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                            '&:hover': { bgcolor: '#f1f5f9 !important' },
                            transition: 'background-color 0.1s'
                          }}
                        >
                          <TableCell>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {yearRange}
                              {isCurrentYearInRange && (
                                <Chip 
                                  label="ACTIVE" 
                                  size="small" 
                                  color="warning" 
                                  sx={{ ml: 1, fontWeight: 'bold' }} 
                                />
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${amnesty.penalty_percent}%`}
                              color={amnesty.penalty_percent === 0 ? "success" : "error"}
                              variant={amnesty.penalty_percent === 0 ? "filled" : "outlined"}
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {amnesty.description || '—'}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {new Date(amnesty.effective_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="Edit">
                                <IconButton size="small" color="info" onClick={() => handleOpenModal(amnesty)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => handleDeleteAmnesty(amnesty)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Paper elevation={0} sx={{ mt: 3, p: 3, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light' }}>
          <Typography variant="subtitle2" color="info.dark" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon fontSize="small" /> How Amnesty Works:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, color: 'info.dark', fontSize: '0.875rem' }}>
            <li>Set a year range to apply amnesty to multiple years at once (e.g., 2000-2005)</li>
            <li>Or set the same year for both fields to apply amnesty to a single year</li>
            <li>The specified penalty percent will be used instead of the calculated penalty</li>
            <li>Set penalty to 0% to waive all penalties for the year range</li>
            <li>Amnesty applies to all properties during payment processing</li>
          </Box>
        </Paper>

        {/* Footer */}
        <Box component="footer" sx={{ py: 6, textAlign: 'center', borderTop: '1px solid #e2e8f0', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Government Portal
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Authorized Personnel Only • Confidential Information
          </Typography>
        </Box>
      </Container>

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          {editingAmnesty ? 'Edit Amnesty' : 'Add New Amnesty'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Start Year"
              type="number"
              name="tax_year_from"
              value={formData.tax_year_from}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="End Year"
              type="number"
              name="tax_year_to"
              value={formData.tax_year_to}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Enter a single year for both fields to apply amnesty to just one year, or a range to apply to multiple years
          </Typography>

          <TextField
            label="Penalty Percent (%)"
            type="number"
            name="penalty_percent"
            value={formData.penalty_percent}
            onChange={handleInputChange}
            fullWidth
            required
            sx={{ mt: 3 }}
            InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
            helperText="Set to 0 to waive all penalties for this year"
          />

          <TextField
            label="Description (Optional)"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 3 }}
            placeholder="e.g., COVID-19 Relief, Special Amnesty 2023"
          />

          {/* Preview */}
          <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Year Range:</strong> {formData.tax_year_from === formData.tax_year_to 
                ? formData.tax_year_from 
                : `${formData.tax_year_from} - ${formData.tax_year_to}`}
              {formData.tax_year_from !== formData.tax_year_to && (
                <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                  ({formData.tax_year_to - formData.tax_year_from + 1} years)
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Penalty:</strong> <Chip label={`${formData.penalty_percent}%`} size="small" color={formData.penalty_percent === 0 ? "success" : "error"} variant="outlined" />
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAmnesty} startIcon={<SaveIcon />}>
            {editingAmnesty ? 'Update' : 'Add'} Amnesty
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
